import Lara from "@/presets/lara";

/**
 * Both containers take the tenant's chrome classes rather than hard-coding them: branded
 * storefronts get a gray header band and a navy close icon, unbranded API tenants keep Lara's
 * teal. Passing them in keeps the branding decision in one place (the component's
 * `useTenantTheming` call) instead of duplicating the ternary per section.
 */
interface ChromeClasses {
  headerBackground: string;
  closeIconText: string;
}

export const excursionCardDetailPresets = (chrome: ChromeClasses) => ({
  ...Lara,
  dialog: {
    ...Lara.dialog,
    header: {
      class: [
        // Flexbox & Alignment
        "flex items-center justify-between",
        "shrink-0",
        // Spacing
        "p-6",
        // Shape
        "border-t-0",
        // Colors
        chrome.headerBackground,
        "font-bold",
        "!text-university-primary",
      ],
    },
    closeButton: {
      class: [
        "relative",
        // Flexbox & Alignment
        "flex items-center justify-center",
        // Size & Spacing
        "mr-2",
        "last:mr-0",
        "w-8 h-8",
        // Shape
        "border-0",
        "rounded-full",
        "overflow-hidden",
        // Colors
        chrome.closeIconText,
        "bg-transparent",
        // Transitions & States
        "transition duration-200 ease-in-out",
        "hover:bg-surface-100",
        "focus:outline-none focus:outline-offset-0",
      ],
    },
    content: () => ({
      class: [
        // Spacing
        "px-6",
        "pb-8",
        "pt-6",
        "rounded-none",
        // Colors & Text
        "bg-surface-0",
        "text-surface-700",
        "text-black",
        "text-base",
        "bg-white",
        // Overflow handling
        "overflow-y-auto",
      ],
    }),
  },
  // Mobile bottom sheet: a PrimeVue Sidebar pinned to `position="bottom"`.
  // PrimeVue's default `ptOptions` are `{ mergeSections: true, mergeProps: false }`, so a
  // section declared here *replaces* the same section of the global Lara preset rather than
  // adding to it — each one below is therefore a complete class list. Sections left out
  // (mask, transition, title) still come from Lara.
  sheet: {
    root: {
      class: [
        // Flexbox — the header stays put while `content` grows and scrolls
        "flex flex-col",
        "relative",
        // Size — leave a sliver of mask visible so the sheet still reads as an overlay.
        // `dvh` keeps the sheet inside the visual viewport while mobile browser chrome
        // is showing, which `vh` does not.
        "w-full",
        "h-[92dvh]",
        "max-h-[92dvh]",
        // Shape
        "border-0",
        "rounded-t-2xl",
        "overflow-hidden",
        "shadow-lg",
        // Colors
        "bg-white",
        "text-surface-700",
        // Transitions
        "transition-transform",
        "duration-300",
        // Misc
        "pointer-events-auto",
      ],
    },
    header: {
      class: [
        // Anchors the absolutely-positioned grab handle rendered in the header slot
        "relative",
        // Flexbox & Alignment
        "flex items-center justify-between",
        "gap-3",
        "shrink-0",
        // Spacing — the top padding reserves room for the grab handle
        "px-4",
        "pt-6",
        "pb-3",
        // Colors
        "bg-white",
        "text-surface-700",
      ],
    },
    closeButton: {
      class: [
        "relative",
        // Flexbox & Alignment
        "flex items-center justify-center",
        "shrink-0",
        // Size
        "w-8 h-8",
        // Shape
        "border-0",
        "rounded-full",
        "overflow-hidden",
        // Colors — matches the desktop dialog's close affordance
        chrome.closeIconText,
        "bg-transparent",
        // Transitions & States
        "transition duration-200 ease-in-out",
        "hover:bg-surface-100",
        "focus:outline-none focus:outline-offset-0",
      ],
    },
    content: {
      class: [
        // Spacing — the body supplies its own gutters
        "p-0",
        "w-full",
        // Growth and Overflow
        "grow",
        "overflow-y-auto",
        "overscroll-contain",
      ],
    },
  },
});
